import APIBase from "./apiBase";
import { Charity } from "./charitiesLib";
import { OnboardingData } from "./types";

export interface DonationStats {
  count: number;
  cents: number;
}

export interface StatsHistoryPoint {
  date: string; // ISO date string
  period: "day" | "month" | "year";
  messagesReceived: number;
  messagesFiltered: number;
  messagesAllowed: number;
  challengesCreated: number;
  challengesViewed: number;
  donationsReceived: DonationStats;
}

export enum StatsPeriod {
  ONE_WEEK = "1W",
  ONE_MONTH = "1M",
  ONE_YEAR = "1Y",
  ALL_TIME = "all",
}

export interface InboxStats {
  period: StatsPeriod;
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  messagesReceived: number;
  messagesFiltered: number;
  messagesAllowed: number;
  challengesCreated: number;
  challengesViewed: number;
  donationsReceived: DonationStats;
  statsHistory: StatsHistoryPoint[];
  statsHistoryGroupingInterval: "day" | "month" | "year";
}

export enum filteringModes {
  DISABLED = "disabled",
  SIMULATING = "simulating",
  FILTERING = "filtering",
}
export type Inbox = {
  id: string;
  email: string;
  displayName: string | null;
  profilePicture: string | null;
  filteringMode: filteringModes;
  challengeEnabled: boolean;
  challengeMessage: string;
  defaultChallengeMessage: string;
  threadBlessingEnabled: boolean;
  bypassEnabled: boolean;
  bypassMessage: string;
  defaultBypassMessage: string;
  lastSyncedAt: Date;
  createdAt: Date;
  updatedAt: Date;
  charity: Charity | null;
  defaultDonation: number;
  minimumDonation: number;
  onboardingBatchId: string | null;
  onboarding: any;
  tokenCreatedAt: Date | null;
  tokenExpiresAt: Date | null;
};

export const get = async (id: string): Promise<Inbox> => {
  const { data } = await APIBase.get<Inbox>(`/inboxes/${id}`);
  return data;
};

export const getStats = async (
  id: string,
  period: string = "1W"
): Promise<InboxStats> => {
  const { data } = await APIBase.get<InboxStats>(`/inboxes/${id}/stats`, {
    params: { period },
  });
  return data;
};

export const update = async (
  id: string,
  updateData: Partial<Inbox>
): Promise<Inbox> => {
  const { data } = await APIBase.patch<Inbox>(`/inboxes/${id}`, updateData);
  return data;
};

export const getHealth = async (
  inboxId: string
): Promise<{ status: "healthy" | "unhealthy" | "atRisk"; message: string }> => {
  const { data } = await APIBase.get<{
    status: "healthy" | "unhealthy" | "atRisk";
    message: string;
  }>(`/inboxes/${inboxId}/healthcheck`);
  console.log(`Health check for inbox ${inboxId} returned ${data.status}`);
  return data;
};

export const bootstrap = async (inboxId: string) => {
  const { data } = await APIBase.post(`/inboxes/${inboxId}/bootstrap`);
  return data;
};

export const getInboxWithOnboarding = async (
  inboxId: string
): Promise<Inbox & { onboarding: OnboardingData }> => {
  const response = await APIBase.get<Inbox & { onboarding: OnboardingData }>(
    `/inboxes/${inboxId}`
  );
  return response.data;
};

export const getOnboardingStats = async (inboxId: string): Promise<any> => {
  const { data } = await APIBase.get<any>(
    `/inboxes/${inboxId}/onboarding-stats`
  );
  return data;
};

export const sendTokenExpiringEmail = async (
  inboxId: string
): Promise<void> => {
  await APIBase.post(`/inboxes/${inboxId}/send-token-expiring-email`);
};

export const sendStatsDigestEmail = async (
  inboxId: string,
  includeWelcomeBlurb?: boolean
): Promise<void> => {
  await APIBase.post(`/inboxes/${inboxId}/send-stats-digest-email`, {
    includeWelcomeBlurb,
  });
};

export const getScopeStatus = async (
  inboxId: string
): Promise<{
  hasActiveScopeIssue: boolean;
  insufficientScopesDetectedAt: string | null;
  insufficientScopesResolvedAt: string | null;
  grantedScopes: string | null;
}> => {
  const { data } = await APIBase.get(`/inboxes/${inboxId}/scope-status`);
  return data;
};

export interface TokenHealthResponse {
  needsReauth: boolean;
  reason: "token_expiring" | "insufficient_scopes" | null;
  expiresAt: string | null;
  inboxEmail: string;
  hasActiveScopeIssue: boolean;
}

export const getTokenHealth = async (
  inboxId: string
): Promise<TokenHealthResponse> => {
  const { data } = await APIBase.get<TokenHealthResponse>(
    `/inboxes/${inboxId}/token-health`
  );
  return data;
};
